<template>
  <!-- ============================================================
       No.7 採択者の手続き追加：採択者側「採択後手続き」タブ
       構成（spec R-1〜R-7）：
         ① 寄付申込書        … フォーマットDL → 記入 → アップロード（任意）
         ② 間接経費免除申請書 … 同上（任意）
         ③ 書類送付先住所     … 宛名/郵便番号/住所/電話番号=必須・FAX=任意
       表示ルールB：①②項目は常に表示。テンプレ設定済み(templates)のみDLリンク。
       value/@input（v-model）・errors（サーバー422由来）・writable（報告期間ロック連動）。
       writable=false のとき入力欄を disabled（グレー）で表示（管理者閲覧でも流用）。
       ============================================================ -->
  <perfect-scrollbar
    :options="scrollOption"
    class="py-3 px-2 ContentsBox">
    <div class="ap-list mx-4">

      <!-- ① 寄付申込書 / ② 間接経費免除申請書（常に表示・任意提出）
           ※「報告の基本情報」タブの成果報告（parts/application/Field.vue + File.vue）と同じレイアウト。 -->
      <div
        v-for="doc in docs" :key="doc.key"
        class="ap-block mb-4 pb-3">
        <b-row
          class="mt-2 mx-4 mb-2"
          align-v="start">
          <div class="label pl-0 mb-0 pr-1 mr-3 mt-2">
            <div class="labelWrap mb-0">
              <label class="apFormLabel mb-0 bold">{{ doc.title }}</label>
            </div>
          </div>
          <div class="mainWidth">
            <!-- ダウンロード：{ファイル名}（設定済みのみ・成果報告 type=3 と同表記）。未設定は行ごと非表示。 -->
            <b-row
              v-if="templates[doc.key]"
              class="ml-1 mt-2">
              <p class="mb-0 form-file-line">
                ダウンロード：
                <b-link
                  @click="$emit('download-template', doc.fileType)"
                  class="mb-0">{{ templates[doc.key].name }}</b-link>
              </p>
            </b-row>
            <!-- ダウンロードの下の注意書き（成果報告と同じ体裁） -->
            <div
              v-if="templates[doc.key]"
              class="ml-1 mt-1">
              <span class="f-13">* 上記ファイルに必要事項を記入し、PDFファイルに変換してアップロードしてください。</span>
            </div>

            <!-- アップロード（成果報告 File.vue と同じ見た目・注意書き） -->
            <div class="my-2 ml-1">
              <b-input-group class="flex flexMiddle">
                <span
                  v-if="!localData[doc.key].name"
                  class="mb-2 fileName">ファイル名：ファイルが選択されていません</span>
                <span
                  v-else-if="localData[doc.key].submitted"
                  class="mb-2 fileName">ファイル名：<b-link
                    @click="$emit('download-uploaded', doc.key)"
                    class="mb-1">{{ localData[doc.key].name }}</b-link></span>
                <span
                  v-else
                  class="mb-2 fileName">ファイル名：{{ localData[doc.key].name }}</span>
                <img
                  v-if="writable && localData[doc.key].name"
                  src="@/assets/svg/close_small.svg"
                  class="tdDeleteBtn ml-3 mb-2"
                  @click="clearUpload(doc)">
              </b-input-group>

              <b-input-group v-if="writable">
                <div
                  @dragover.prevent="onDragOver(doc, true)"
                  @dragleave.prevent="onDragOver(doc, false)"
                  @drop.prevent="onDrop($event, doc)"
                  :class="[dragging[doc.key] ? 'is-dragging' : '', 'drop', 'flex', 'flexMiddle', 'flexCenter']">
                  <span>
                    <span
                      :class="[dragging[doc.key] ? 'is-dragging' : '', 'dragText', 'fw-6']"
                      >ここにファイルをドラッグ＆ドロップ または</span>
                    <label
                      :for="`ap_file_${doc.key}`"
                      class="fileText fw-6 selectFile">［ ファイルを選択 ］</label>
                    <p :class="[dragging[doc.key] ? 'is-dragging' : '', 'fileNotation', 'mb-0']">
                      （ファイルを再アップロードした場合アップロード済みのファイルは上書きされます）
                    </p>
                    <input type="file" style="display:none;"
                      :id="`ap_file_${doc.key}`"
                      @change="onSelectFile($event, doc)">
                  </span>
                </div>
              </b-input-group>
            </div>
          </div>
        </b-row>
      </div>

      <!-- その他添付（複数・任意・テンプレなし・形式自由・No.7 H-3／間接経費免除申請書の後） -->
      <div class="ap-block mb-4 pb-3">
        <b-row class="mt-2 mx-4 mb-2" align-v="start">
          <div class="label pl-0 mb-0 pr-1 mr-3 mt-2">
            <div class="labelWrap mb-0">
              <label class="apFormLabel mb-0 bold">その他添付</label>
            </div>
          </div>
          <div class="mainWidth">
            <div class="my-2 ml-1">
              <!-- 既存（DB）ファイル：DLリンク＋×削除 -->
              <div
                v-for="f in localData.otherExisting" :key="`oe_${f.id}`"
                class="mb-1 flex flexMiddle">
                <span class="fileName mb-0">ファイル名：<b-link
                  @click="$emit('download-other', f.id)"
                  class="mb-0">{{ f.name }}</b-link></span>
                <img
                  v-if="writable"
                  src="@/assets/svg/close_small.svg"
                  class="tdDeleteBtn ml-3"
                  @click="removeOtherExisting(f.id)">
              </div>
              <!-- 新規選択ファイル：×取消 -->
              <div
                v-for="(o, i) in localData.otherNew" :key="`on_${i}`"
                class="mb-1 flex flexMiddle">
                <span class="fileName mb-0">ファイル名：{{ o.name }}</span>
                <img
                  v-if="writable"
                  src="@/assets/svg/close_small.svg"
                  class="tdDeleteBtn ml-3"
                  @click="removeOtherNew(i)">
              </div>
              <span
                v-if="!localData.otherExisting.length && !localData.otherNew.length"
                class="mb-2 fileName">ファイル名：ファイルが選択されていません</span>

              <b-input-group v-if="writable" class="mt-2">
                <div
                  @dragover.prevent="onOtherDragOver(true)"
                  @dragleave.prevent="onOtherDragOver(false)"
                  @drop.prevent="onOtherDrop($event)"
                  :class="[otherDragging ? 'is-dragging' : '', 'drop', 'flex', 'flexMiddle', 'flexCenter']">
                  <span>
                    <span
                      :class="[otherDragging ? 'is-dragging' : '', 'dragText', 'fw-6']"
                      >ここにファイルをドラッグ＆ドロップ または</span>
                    <label
                      for="ap_file_other"
                      class="fileText fw-6 selectFile">［ ファイルを選択 ］</label>
                    <p :class="[otherDragging ? 'is-dragging' : '', 'fileNotation', 'mb-0']">
                      （複数選択できます）
                    </p>
                    <input type="file" multiple style="display:none;"
                      id="ap_file_other"
                      @change="onSelectOther($event)">
                  </span>
                </div>
              </b-input-group>
            </div>
          </div>
        </b-row>
      </div>

      <!-- ③ 書類送付先住所 -->
      <div class="ap-block ap-address mb-2 pb-3">
        <!-- 宛名（必須） -->
        <b-row class="mt-2">
          <div class="label pl-0 mb-0 mr-10px">
            <span class="mr-2 badge badge-required">必須</span>
            <label class="mb-0 bold">書類送付先 宛名</label>
          </div>
          <div>
            <b-form-input
              :disabled="!writable"
              class="long"
              :value="localData.addressee"
              @input="v => setField('addressee', v)"
              maxlength="255"
              placeholder="例）〇〇大学 研究支援課 御中"/>
            <div v-if="errors.addressee" class="errText">{{ errors.addressee }}</div>
          </div>
        </b-row>
        <hr>

        <!-- 郵便番号（必須） -->
        <b-row class="mt-2">
          <div class="label pl-0 mb-0 mr-10px">
            <span class="mr-2 badge badge-required">必須</span>
            <label class="mb-0 bold">書類送付先 郵便番号</label>
          </div>
          <div>
            <b-form-input
              :disabled="!writable"
              :value="localData.postalCode"
              @input="updatePostalCode"
              maxlength="7"
              placeholder="例）5410041"/>
            <div v-if="errors.postal_code" class="errText">{{ errors.postal_code }}</div>
          </div>
        </b-row>
        <hr>

        <!-- 住所（必須） -->
        <b-row class="mt-2">
          <div class="label pl-0 mb-0 mr-10px">
            <span class="mr-2 badge badge-required">必須</span>
            <label class="mb-0 bold">書類送付先 住所</label>
          </div>
          <div>
            <b-form-input
              :disabled="!writable"
              class="long"
              :value="localData.address"
              @input="v => setField('address', v)"
              maxlength="500"
              placeholder="例）大阪府大阪市中央区xx町1-2-3"/>
            <div v-if="errors.address" class="errText">{{ errors.address }}</div>
          </div>
        </b-row>
        <hr>

        <!-- 電話番号（必須） -->
        <b-row class="mt-2">
          <div class="label pl-0 mb-0 mr-10px">
            <span class="mr-2 badge badge-required">必須</span>
            <label class="mb-0 bold">書類送付先 電話番号</label>
          </div>
          <div>
            <b-form-input
              :disabled="!writable"
              class="middle"
              :value="localData.tel"
              @input="v => setField('tel', v)"
              maxlength="16"
              placeholder="例）0662223380"/>
            <div v-if="errors.tel" class="errText">{{ errors.tel }}</div>
          </div>
        </b-row>
        <hr>

        <!-- FAX番号（任意） -->
        <b-row class="mt-2">
          <div class="label pl-0 mb-0 mr-10px">
            <span class="hidden mr-2 badge badge-required">必須</span>
            <label class="mb-0 bold">書類送付先 FAX番号</label>
          </div>
          <div>
            <b-form-input
              :disabled="!writable"
              class="middle"
              :value="localData.fax"
              @input="v => setField('fax', v)"
              maxlength="16"
              placeholder="例）0662223380"/>
          </div>
        </b-row>
      </div>
    </div>
  </perfect-scrollbar>
</template>

<script>
import { Core as YubinBangoCore } from 'yubinbango-core';

export default {
  name: 'reportAdoptionProcedure',
  props: {
    // v-model：{ addressee, postalCode, address, tel, fax, donation:{name,submitted,delete}, exemption:{...} }
    value: { type: Object, default: () => ({}) },
    // ①②テンプレ（表示ルールB）：{ donation: {name}|null, exemption: {name}|null }。null=未設定＝非表示
    templates: { type: Object, default: () => ({ donation: null, exemption: null }) },
    // 入力可（採択者）／変更不可・グレー（管理者・閲覧）
    writable: { type: Boolean, default: true },
    // ③必須エラー（サーバー422由来）：{ addressee, postal_code, address, tel }
    errors: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      scrollOption: {
        wheelPropagation: true,
        suppressScrollX: true,
      },
      docs: [
        { key: 'donation', title: '寄付申込書', fileType: 4 },
        { key: 'exemption', title: '間接経費免除申請書', fileType: 5 },
      ],
      localData: this.normalize(),
      dragging: { donation: false, exemption: false },
      otherDragging: false,
    };
  },
  watch: {
    value: {
      handler() {
        this.localData = this.normalize();
      },
      deep: true,
    },
  },
  methods: {
    normalize() {
      const v = this.value || {};
      const normFile = f => ({
        name: (f && f.name) || '',
        submitted: !!(f && f.submitted),
        delete: !!(f && f.delete),
      });
      return {
        addressee: v.addressee || '',
        postalCode: v.postalCode || '',
        address: v.address || '',
        tel: v.tel || '',
        fax: v.fax || '',
        donation: normFile(v.donation),
        exemption: normFile(v.exemption),
        // その他添付（複数）：既存DB＝otherExisting、新規＝otherNew(File保持)、削除id＝otherDeleteIds
        otherExisting: (v.otherFiles || []).map(f => ({ id: f.id, name: f.name })),
        otherNew: (v.otherNew || []).map(o => ({ name: o.name, file: o.file })),
        otherDeleteIds: [...(v.otherDeleteIds || [])],
      };
    },
    emit() {
      this.$emit('input', {
        addressee: this.localData.addressee,
        postalCode: this.localData.postalCode,
        address: this.localData.address,
        tel: this.localData.tel,
        fax: this.localData.fax,
        donation: { ...this.localData.donation },
        exemption: { ...this.localData.exemption },
        // その他添付は value に round-trip（新規Fileは adoptionData.otherNew に保持し親が multipart 添付）
        otherFiles: this.localData.otherExisting.map(f => ({ id: f.id, name: f.name })),
        otherNew: this.localData.otherNew.map(o => ({ name: o.name, file: o.file })),
        otherDeleteIds: [...this.localData.otherDeleteIds],
      });
    },
    setField(key, val) {
      this.localData[key] = val;
      this.emit();
    },
    updatePostalCode(value) {
      this.localData.postalCode = value;
      // 郵便番号→住所を自動反映（申請者情報と同じ挙動・任意の利便機能）
      if (value.length > 6) {
        new YubinBangoCore(value, (addr) => { // eslint-disable-line no-new
          const full = addr.region + addr.locality + addr.street;
          if (full) {
            this.localData.address = full;
            this.emit();
          }
        });
      }
      this.emit();
    },
    onSelectFile(event, doc) {
      if (event.target.files.length === 0) {
        return;
      }
      const file = event.target.files[0];
      this.setUploaded(doc.key, file);
      // 同じファイルを選び直しても @change が発火するよう input をクリア
      const fileInput = event.target;
      fileInput.value = '';
    },
    onDrop(event, doc) {
      this.dragging[doc.key] = false;
      const fileList = event.dataTransfer ? event.dataTransfer.files : event.target.files;
      if (!fileList || fileList.length === 0) {
        return;
      }
      this.setUploaded(doc.key, fileList[0]);
    },
    setUploaded(key, file) {
      this.localData[key].name = file.name;
      this.localData[key].submitted = false; // 新規選択＝未保存のためDLリンクは出さない
      this.localData[key].delete = false;
      this.$emit('select-file', { key, file });
      this.emit();
    },
    onDragOver(doc, isDragging) {
      this.dragging[doc.key] = isDragging;
    },
    clearUpload(doc) {
      this.localData[doc.key].name = '';
      this.localData[doc.key].submitted = false;
      // ×＝「このファイルを消す」操作。常に削除フラグを立てる。
      // 新規ファイルを選ぶと submitted が false に落ちるため wasSubmitted 判定では
      // 「DB提出済み → 新規選択 → ×」で削除フラグを取りこぼす（DBファイルが残る）。
      // DBファイルが無い場合も backend の削除分岐は無害（null カラムを null にするだけ）。
      this.localData[doc.key].delete = true;
      this.$emit('select-file', { key: doc.key, file: null });
      this.emit();
    },
    // その他添付：複数選択で追加（既存に append）
    onSelectOther(event) {
      const input = event.target;
      Array.from(input.files || []).forEach((file) => {
        this.localData.otherNew.push({ name: file.name, file });
      });
      input.value = ''; // 同名再選択でも @change が発火するようクリア
      this.emit();
    },
    onOtherDrop(event) {
      this.otherDragging = false;
      const list = event.dataTransfer ? event.dataTransfer.files : event.target.files;
      Array.from(list || []).forEach((file) => {
        this.localData.otherNew.push({ name: file.name, file });
      });
      this.emit();
    },
    onOtherDragOver(isDragging) {
      this.otherDragging = isDragging;
    },
    // 既存（DB）ファイルの×：一覧から外し、削除id へ積む（保存時に論理削除）
    removeOtherExisting(id) {
      this.localData.otherExisting = this.localData.otherExisting.filter(f => f.id !== id);
      if (!this.localData.otherDeleteIds.includes(id)) {
        this.localData.otherDeleteIds.push(id);
      }
      this.emit();
    },
    // 新規選択の×：追加を取り消す
    removeOtherNew(index) {
      this.localData.otherNew.splice(index, 1);
      this.emit();
    },
  },
};
</script>

<style scoped>
.ap-list {
  max-width: 1000px;
}
.ap-block {
  border-bottom: 1px dashed #d6d6d6;
}
.ap-address {
  border-bottom: none;
}
/* ①② ラベル左＋メイン右（成果報告 parts/application/Field.vue と同じ） */
.labelWrap {
  display: inline-block;
}
.apFormLabel {
  white-space: pre-line;
}
.mainWidth {
  flex: 1 1 0;
  min-width: 0;
}
.form-file-line {
  width: 100%;
}
.f-13 {
  font-size: 13px;
}
.errText {
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 4px;
}
.fileName {
  margin-top: 16px;
}
.tdDeleteBtn {
  cursor: pointer;
}
/* ファイルアップロード欄（parts/application/File.vue と同一の見た目） */
.drop {
  height: 120px;
  width: 100%;
  background-color: #F3F3F3;
  border-color: #CCC;
  border-style: dashed;
  border-radius: 10px;
  border-width: 1px;
}
.dragText {
  color: #BBB;
}
.fileText {
  color: #0A8EA7;
}
.fileNotation {
  color: #BBB;
}
.is-dragging.drop {
  background-color: #E6E6E6;
  border-color: #6E6E6E;
}
.is-dragging {
  color: #6E6E6E;
}
.selectFile {
  cursor: pointer;
}
.selectFile:hover {
  text-decoration: underline;
}
/* ラベル左カラム（230px固定。成果報告 Field.vue と同じ・住所欄と共通） */
.label {
  flex: 0 0 230px;
  width: 230px;
  max-width: 230px;
}
.mr-10px {
  margin-right: 10px;
}
.middle {
  width: 400px;
}
.long {
  width: 750px;
}
</style>
